import { useQuery } from '@tanstack/react-query';

import {
  fundEndpoints as contractFundEndpoints,
  type FundListItem,
  indicatorEndpoints,
} from '@d3/contract';

import { api, createQueryKeys } from '@shared/api';

import type { Fund, FundCutoff, FundOption } from '../model/fund';
import type { TotalSummary } from '../model/total-summary';
import { toFundDetail } from './fund-detail.mapper';
import { toFund, toFundOption } from './fund-list.mapper';
import { toTotalSummary } from './total-summary.mapper';

export const fundKeys = createQueryKeys('fund');

/**
 * 펀드 목록 — 지표 포함(`GET /funds?withIndicators=true`). 메인 · 펀드 목록처럼 지표를 쓰는 화면용.
 * 경량 조회(useFundOptionsQuery)와 캐시 key 를 나눠 서로 섞이지 않는다.
 */
export function useFundsQuery() {
  return useQuery({
    throwOnError: true,
    queryKey: fundKeys.list({ withIndicators: true }),
    queryFn: ({ signal }) =>
      api.request(contractFundEndpoints.listFunds, { query: { withIndicators: true }, signal }),
    select: (data): readonly Fund[] => data.items.map(toFund),
  });
}

/**
 * 펀드 경량 목록(`GET /funds`) — Select · 드롭다운처럼 id · 이름만 필요한 곳.
 * 지표를 받지 않아 서버 · 네트워크가 가볍다. 지표 목록(useFundsQuery)과 캐시 key 가 다르다.
 */
export function useFundOptionsQuery() {
  return useQuery({
    throwOnError: true,
    queryKey: fundKeys.list({ withIndicators: false }),
    queryFn: ({ signal }) => api.request(contractFundEndpoints.listFunds, { signal }),
    select: (data: { items: FundListItem[] }): readonly FundOption[] =>
      data.items.map(toFundOption),
  });
}

/**
 * 펀드 상세(`GET /funds/:id`, @d3/contract). 펀드 상세(S05)의 구획들 · 펀드 수정(S07)의 처음 값이
 * 같은 key 를 쓴다 — 레거시(useFundDetail · useFundEdit)처럼 화면마다 따로 받아 상태에 복사하지 않는다.
 * enabled=false 면 부르지 않는다(펀드 추가 화면). throwOnError=false 는 구획 밖(팝업 · 폼의 선택지)에서 —
 * 오류여도 화면을 내리지 않고 부르는 쪽이 자료 없음으로 다룬다. 지표 재구성은 mapper 가 흡수한다.
 */
export function useFundQuery(
  idx: number,
  options: { enabled?: boolean; throwOnError?: boolean } = {},
) {
  return useQuery({
    throwOnError: options.throwOnError ?? true,
    queryKey: fundKeys.detail(idx),
    queryFn: ({ signal }) =>
      api.request(contractFundEndpoints.getFund, { params: { id: idx }, signal }),
    select: toFundDetail,
    enabled: options.enabled ?? true,
  });
}

/**
 * 펀드별 공개 분기(`GET /indicators/fund-cutoffs`, @d3/contract) — 수동 메일 발송(S17)의 LP 대상 "공개 분기" 칸.
 * 공개된 리포트가 없는 펀드(null)는 뺀다. 리포트 공개를 바꾸면(`features/report-publish`) 무효화된다.
 */
export function useFundCutoffsQuery() {
  return useQuery({
    throwOnError: true,
    queryKey: fundKeys.scope('cutoff'),
    queryFn: ({ signal }) => api.request(indicatorEndpoints.listFundReportCutoffs, { signal }),
    select: (data): readonly FundCutoff[] =>
      data.items.flatMap((row) =>
        row.year === null || row.quarter === null
          ? []
          : [{ fundIdx: row.fundId, year: row.year, quarter: row.quarter }],
      ),
  });
}

/** 메인 전체 요약(`GET /indicators/summary`). 계약 필드명은 mapper 가 흡수한다 */
export function useTotalSummaryQuery() {
  return useQuery({
    throwOnError: true,
    queryKey: fundKeys.scope('summary'),
    queryFn: ({ signal }) => api.request(indicatorEndpoints.getTotalSummary, { signal }),
    select: (data: Parameters<typeof toTotalSummary>[0]): TotalSummary => toTotalSummary(data),
  });
}
